import { Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import { Autosizer, Scrollbar } from 'app/components';
import React, { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';

const ChecklistTemplateHistoryList = () => {
  const { history } = useSelector((state) => state.checklist.templateSelectedItem);

  return (
    <Box
      component='div'
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        '.simplebar-offset > div': {
          '&::-webkit-scrollbar': {
            width: 0,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            boxShadow: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
        '.simplebar-horizontal': {
          right: '10px',
        },
        '.simplebar-vertical': {
          top: '30px',
          bottom: '0',
        },
        '.MuiListItem-root': {
          width: '100%',
          userSelect: 'none',
          padding: theme.spacing(0),
          borderRadius: '0',
        },
      })}>
      <Autosizer>
        {({ height, width }) => (
          <Box sx={{ height, width }}>
            <Scrollbar>
              {({ scrollableNodeRef, contentNodeRef }) => (
                <FixedSizeList
                  height={height}
                  itemSize={24}
                  overscanCount={10}
                  itemCount={history.length}
                  innerRef={contentNodeRef}
                  outerRef={scrollableNodeRef}
                  innerElementType={InnerElementType}
                  itemData={{
                    items: history,
                  }}>
                  {ItemRender}
                </FixedSizeList>
              )}
            </Scrollbar>
          </Box>
        )}
      </Autosizer>
    </Box>
  );
};

const InnerElementType = forwardRef(({ children, style, ...rest }, ref) => (
  <List
    ref={ref}
    disablePadding
    sx={{
      height: style.height,
      minHeight: '100%',
    }}
    {...rest}>
    <ListItem
      sx={(theme) => ({
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        height: style.height,
        minHeight: '100%',
        zIndex: '2',
        pointerEvents: 'none',
        padding: theme.spacing(0),
        '.MuiListItemText-root': {
          display: 'flex',
          justifyContent: 'space-between',
          height: '100%',
          position: 'relative',
          flex: 'none',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: '0',
            right: '0',
            height: '100%',
            bgcolor: theme.palette.divider,
            width: '1px',
            zIndex: '1',
            pointerEvents: 'none',
          },
          '&:last-of-type': {
            flex: '1',
            '&:before': {
              display: 'none',
            },
          },
        },
      })}>
      {config.checklistTemplateHistoryColumns.map((column) => (
        <ListItemText key={column.name} style={{ width: column.width }} />
      ))}
    </ListItem>
    {children}
  </List>
));

const ItemRender = ({ data, index, style }) => {
  const { items } = data;

  return (
    <ListItem
      key={index}
      disablePadding
      style={style}
      sx={(theme) => ({
        width: '100%',
        marginTop: '0px',
        userSelect: 'none',
        borderRadius: '0',
        '&:hover': {
          '.MuiListItemText-root': {
            cursor: 'pointer',
          },
        },
        '&.Mui-selected': {
          '.MuiListItemText-root': {
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
          '.MuiCheckbox-root': {
            color: theme.palette.primary.contrastText,
            '&.Mui-checked': {
              color: theme.palette.primary.contrastText,
            },
          },
        },
        '.MuiListItemText-root': {
          display: 'flex',
          flex: 'none',
          height: '100%',
          margin: theme.spacing(0),
          padding: theme.spacing(0.5, 1),
          alignItems: 'center',
          '&:first-of-type': {
            paddingLeft: theme.spacing(2),
            svg: {
              fontSize: '16px',
              marginRight: '3px',
            },
          },
          '&:last-of-type': {
            flex: '1',
            paddingRight: theme.spacing(2),
          },
          '.MuiTypography-root': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          },
          '.MuiCheckbox-root': {
            padding: theme.spacing(0),
            marginLeft: '-2px',
            svg: {
              fontSize: '18px',
            },
          },
        },
        ...((items.length - (items.length - 1 - index)) % 2 === 0 && {
          '& .MuiListItemText-root': {
            bgcolor: 'background.default',
          },
        }),
      })}>
      {config.checklistTemplateHistoryColumns.map((column) => (
        <ListItemText key={column.name} sx={{ minWidth: column.width }}>
          {<Typography>{column.format ? column.format(items[index][column.name]) : items[index][column.name]}</Typography>}
        </ListItemText>
      ))}
    </ListItem>
  );
};

export default ChecklistTemplateHistoryList;
